<!-- src/shared/components/layout/ManagementHeader.vue -->

<!--
  Management Header Component

  Features:
  - Wider container (max-w-4xl instead of max-w-2xl)
  - Default slot for custom content (e.g., context bars)
  - Toggleable primary nav via displayPrimaryNav prop
  - Cleaner separation between brand and navigation
-->

<script setup lang="ts">
  import MastHead from '@/shared/components/layout/MastHead.vue';
  import ImprovedPrimaryNav from '@/shared/components/navigation/ImprovedPrimaryNav.vue';
  import { useHeaderEnabled } from '@/shared/composables/useHeaderEnabled';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import type { LayoutProps } from '@/types/ui/layouts';
  import { storeToRefs } from 'pinia';

  const props = withDefaults(defineProps<LayoutProps>(), {
    displayMasthead: true,
    displayNavigation: true,
    displayPrimaryNav: true,
    colonel: false,
  });

  const bootstrapStore = useBootstrapStore();
  const { authenticated } = storeToRefs(bootstrapStore);

  // Operator-level header gate (HEADER_ENABLED). When disabled, the entire
  // <header> banner landmark collapses — no empty landmark, no padding band.
  const { headerEnabled } = useHeaderEnabled();
</script>

<template>
  <header
    v-if="headerEnabled"
    class="border-b border-day-line bg-day dark:border-white/[0.13] dark:bg-night">
    <!-- Single row header with Logo, Context Switchers, and User Menu -->
    <div :class="['container mx-auto min-w-[320px] px-4', colonel ? 'max-w-6xl' : 'max-w-4xl']">
      <div class="py-3">
        <MastHead v-if="displayMasthead" v-bind="props">
          <!-- Pass context switchers to be rendered inline with logo -->
          <template #context-switchers>
            <slot></slot>
          </template>
        </MastHead>
      </div>
    </div>

    <!-- Primary Navigation Bar (for authenticated users) - Hidden on mobile -->
    <div
      v-if="authenticated && displayNavigation && displayPrimaryNav"
      class="hidden border-t border-day-line bg-day-100 md:block dark:border-white/[0.13] dark:bg-night-100">
      <div :class="['container mx-auto min-w-[320px] px-4', colonel ? 'max-w-6xl' : 'max-w-4xl']">
        <ImprovedPrimaryNav />
      </div>
    </div>
  </header>
</template>
